import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import { zoom, zoomIdentity } from 'd3-zoom';
import { feature } from 'topojson-client';
import { EUROPE_CENTROIDS } from '../lib/europeCountryCentroids';
import './EuropeLiveVisitorsMap.css';

/** Natural Earth 110m landen (TopoJSON). */
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json';

const MAP_W = 880;
const MAP_H = 620;

/**
 * world-atlas 2.x landen hebben alleen `properties.name` + numeriek `id` (ISO 3166-1 numeric).
 * Deze set = Europa (+ Kaukasus, Kazachstan, Turkije, Rusland; microstaten; zonder Groenland).
 */
const EUROPE_ISO_NUMERIC = new Set([
  8, 20, 40, 56, 70, 100, 112, 191, 196, 203, 208, 233, 246, 250, 276, 300, 348, 352, 372, 380, 428, 438, 440, 442, 470, 492, 498, 499, 528, 578, 616, 620, 642, 643, 674, 688, 703, 705, 724, 752, 756, 792, 804, 826, 336,
  398, 268, 51, 31, 807, 234, 292, 831, 832, 833, 248, 744, 900,
]);

/**
 * Opstart: dit lon/lat-vak vullen met fitExtent (Mercator), daarna zuidelijk Spanje naar de onderkant schuiven.
 * NL · DE · ES · IT · FR vallen hier ruim binnen.
 */
const START_FRAME = {
  type: 'Polygon',
  coordinates: [
    [
      [-9.5, 35.8],
      [18.8, 35.8],
      [18.8, 56.0],
      [-9.5, 56.0],
      [-9.5, 35.8],
    ],
  ],
};

/** Zuidpunt vasteland Z-Spanje (Tarifa-omgeving) — na inzoomen op onderkant SVG uitlijnen. */
const SOUTH_SPAIN_LONLAT = [-5.55, 35.92];
const START_BOTTOM_PAD = 8;

/** Extra inzoomen na fitExtent (Mercator-schaal × factor), daarna weer onderkant uitlijnen. */
const START_ZOOM_BOOST = 2.38;

/**
 * Verschuift de Mercator-translate zodat het punt zuidelijk Spanje op y = mapHeight - padding ligt.
 */
function anchorSouthSpainToBottom(p, mapHeight) {
  const xy = p(SOUTH_SPAIN_LONLAT);
  if (!xy || !Number.isFinite(xy[0]) || !Number.isFinite(xy[1])) return;
  const [tx, ty] = p.translate();
  p.translate([tx, ty + (mapHeight - START_BOTTOM_PAD - xy[1])]);
}

function isEuropeFeature(f) {
  const idRaw = f.id;
  const idNum = idRaw != null && idRaw !== '' ? Number(idRaw) : NaN;
  if (!Number.isNaN(idNum) && EUROPE_ISO_NUMERIC.has(idNum)) return true;
  const name = String(f.properties?.name || '').toLowerCase();
  if (name.includes('kosovo')) return true;
  return false;
}

function radiusForVisitors(n) {
  if (n >= 20) return 3.2;
  if (n >= 10) return 2.75;
  if (n >= 5) return 2.4;
  if (n >= 2) return 2.1;
  return 1.85;
}

const DOT_RADIUS = 1.85;

/** Zelfde prioriteit als Attributie live-rijen: checkout boven winkelmand. */
function liveDotFunnelTier(d) {
  if (!d) return 'browse';
  if (d.visited_checkout) return 'checkout';
  if (d.visited_cart) return 'cart';
  return 'browse';
}

const LIVE_DOT_FUNNEL_STYLES = {
  browse: { halo: '#ef4444', haloOp: 0.22, core: '#dc2626' },
  cart: { halo: '#4ade80', haloOp: 0.26, core: '#22c55e' },
  checkout: { halo: '#34d399', haloOp: 0.28, core: '#059669' },
};

function countryNameNl(iso2) {
  if (!iso2) return '';
  try {
    const dn = new Intl.DisplayNames(['nl'], { type: 'region' });
    return dn.of(iso2);
  } catch {
    return iso2;
  }
}

/** Regels voor tooltip winkelwagen (live); `cart` komt van GET /dashboard/live-visitors-europe. */
function formatCartForMapTooltip(cart) {
  if (!cart || typeof cart !== 'object') return null;
  const lines = Array.isArray(cart.lines) ? cart.lines : [];
  const itemCount = Number(cart.item_count) || 0;
  if (lines.length === 0 && itemCount <= 0) {
    return ['Winkelwagen: leeg'];
  }
  let fmt = null;
  const cur = cart.currency && String(cart.currency).trim().slice(0, 3);
  if (cur) {
    try {
      fmt = new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: 2,
      });
    } catch {
      fmt = null;
    }
  }
  const out = [];
  lines.slice(0, 6).forEach((l) => {
    const name = (l.name || 'Product').trim().slice(0, 48) || 'Product';
    const q = Number(l.quantity) || 0;
    const lt = typeof l.line_total === 'number' ? l.line_total : parseFloat(l.line_total);
    const money =
      fmt != null && Number.isFinite(lt) ? fmt.format(lt) : Number.isFinite(lt) && lt !== 0 ? String(lt) : '';
    out.push(money ? `${name} ×${q} — ${money}` : `${name} ×${q}`);
  });
  if (lines.length > 6) {
    out.push(`… +${lines.length - 6} regels`);
  }
  return out;
}

export default function EuropeLiveVisitorsMap({
  dots,
  points,
  loading,
  error,
  updatedAt,
  windowMinutes = 5,
}) {
  const svgRef = useRef(null);
  const zoomLayerRef = useRef(null);
  const [hoverTip, setHoverTip] = useState(null);
  const [europeFeatures, setEuropeFeatures] = useState([]);
  const [geoLoadError, setGeoLoadError] = useState('');

  /** Mercator: fit op kernkader, extra inzoomen, zuidelijk Spanje onderaan. */
  const { projection, path } = useMemo(() => {
    const p = geoMercator().precision(0.1);
    if (europeFeatures.length > 0) {
      try {
        p.fitExtent(
          [
            [2, 2],
            [MAP_W - 2, MAP_H - 2],
          ],
          START_FRAME
        );
      } catch {
        p.center([5, 46]).scale(2400).translate([MAP_W / 2, MAP_H / 2]);
      }
      p.scale(p.scale() * START_ZOOM_BOOST);
      anchorSouthSpainToBottom(p, MAP_H);
    } else {
      p.center([5, 46]).scale(520).translate([MAP_W / 2, MAP_H / 2]);
    }
    return { projection: p, path: geoPath(p) };
  }, [europeFeatures]);

  useEffect(() => {
    let cancelled = false;
    setGeoLoadError('');
    fetch(GEO_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`Kaartdata (${r.status})`);
        return r.json();
      })
      .then((topo) => {
        if (cancelled || !topo?.objects?.countries) return;
        const coll = feature(topo, topo.objects.countries);
        const feats = (coll.features || []).filter(isEuropeFeature);
        setEuropeFeatures(feats);
      })
      .catch((e) => {
        if (!cancelled) setGeoLoadError(e.message || 'Kaartdata laden mislukt');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    const el = svgRef.current;
    const layer = zoomLayerRef.current;
    if (!el || !layer) return undefined;
    const svg = select(el);
    const g = select(layer);
    if (europeFeatures.length === 0) {
      g.attr('transform', null);
      return undefined;
    }
    const z = zoom()
      .extent([
        [0, 0],
        [MAP_W, MAP_H],
      ])
      .scaleExtent([0.18, 14])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });
    svg.call(z);
    svg.call(z.transform, zoomIdentity);
    return () => {
      svg.on('.zoom', null);
    };
  }, [europeFeatures]);

  const dotList = Array.isArray(dots) ? dots : [];
  const useDots = dotList.length > 0;

  const totalCentroid = Array.isArray(points)
    ? points.reduce((s, p) => s + (p.visitors || 0), 0)
    : 0;
  const total = useDots ? dotList.length : totalCentroid;

  const markers = useMemo(() => {
    if (useDots) {
      return dotList
        .filter((d) => d && d.country && Number.isFinite(d.lat) && Number.isFinite(d.lon))
        .map((d) => {
          const city = d.city ? String(d.city).trim() : '';
          const plaatsen = city || 'Geen stadsnaam in tracking (alleen land)';
          const land = countryNameNl(d.country);
          const tipTitle = `${land} (${d.country})`;
          const tipSub = '1 bezoeker';
          const cartLines = formatCartForMapTooltip(d.cart);
          const funnel = liveDotFunnelTier(d);
          const dotColors = LIVE_DOT_FUNNEL_STYLES[funnel] || LIVE_DOT_FUNNEL_STYLES.browse;
          return {
            mode: 'dot',
            key: d.visitor_id || `${d.country}-${d.lat}-${d.lon}`,
            lon: d.lon,
            lat: d.lat,
            hoverId: d.visitor_id || `${d.country}-${d.lat}-${d.lon}`,
            r: DOT_RADIUS,
            tipTitle,
            tipSub,
            plaatsen,
            cartLines,
            funnel,
            dotHaloFill: dotColors.halo,
            dotHaloOpacity: dotColors.haloOp,
            dotCoreFill: dotColors.core,
          };
        });
    }
    return (Array.isArray(points) ? points : [])
      .filter((p) => p && p.country && EUROPE_CENTROIDS[p.country])
      .map((p) => {
        const [lon, lat] = EUROPE_CENTROIDS[p.country];
        const cities = Array.isArray(p.cities) ? p.cities : [];
        const plaatsen =
          cities.length > 0 ? cities.join(', ') : 'Geen stadsnaam in tracking (alleen land)';
        const land = countryNameNl(p.country);
        const tipTitle = `${land} (${p.country})`;
        const tipSub = p.visitors === 1 ? '1 bezoeker' : `${p.visitors} bezoekers`;
        return {
          mode: 'agg',
          key: p.country,
          hoverId: p.country,
          lon,
          lat,
          visitors: p.visitors || 0,
          r: radiusForVisitors(p.visitors),
          tipTitle,
          tipSub,
          plaatsen,
        };
      })
      .sort((a, b) => b.visitors - a.visitors);
  }, [useDots, dotList, points]);

  const mapError = error || geoLoadError;

  return (
    <div className="europe-live-map">
      <div className="europe-live-map-header">
        <h3 className="europe-live-map-title">Live bezoekers (Europa)</h3>
        {updatedAt && (
          <span className="europe-live-map-updated">
            {new Date(updatedAt).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
      <p className="europe-live-map-source">
        Zelfde data als <strong>Attributie → Live</strong> (laatste hit per bezoeker, {windowMinutes} min, zonder bots).
        <strong>Groene stip</strong> = in dit venster een pagina in <strong>winkelmand</strong> of <strong>checkout</strong> bezocht
        (donkergroen = checkout). Met de WooCommerce-trackingplugin (≥1.1.6) staat de winkelwagen ook in de tooltip.
        Stippen staan op geschatte plek (IP-geo of stad); bij inzoomen zie je meerdere bezoekers in hetzelfde land
        los van elkaar. Landvormen komen automatisch online (Natural Earth via cdn.jsdelivr.net). Bij een lege zee:
        firewall of adblocker controleren.
      </p>
      <p className="europe-live-map-zoom-hint">
        Opstart: <strong>zuidelijk Spanje</strong> ligt aan de <strong>onderkant</strong> van de kaart; NL · DE · FR · IT
        vallen daarboven in beeld. Scroll naar <strong>buiten</strong> voor meer Europa; scroll naar binnen of sleep om te
        verschuiven.
      </p>
      {mapError && <p className="europe-live-map-error">{mapError}</p>}
      <div className="europe-live-map-body">
        <div className="europe-live-map-svg-wrap">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${MAP_W} ${MAP_H}`}
            width="100%"
            className="europe-live-map-composable"
            style={{ touchAction: 'none' }}
            role="img"
            aria-label="Kaart live bezoekers Europa"
          >
            <g ref={zoomLayerRef}>
              {europeFeatures.map((f, idx) => (
                <path
                  key={f.id != null ? String(f.id) : `f-${idx}`}
                  d={path(f) || ''}
                  fill="#d8e0e8"
                  stroke="#8a9ba8"
                  strokeWidth={0.35}
                  style={{ outline: 'none' }}
                />
              ))}
              {markers.map((m) => {
                const xy = projection([m.lon, m.lat]);
                if (!xy || !Number.isFinite(xy[0]) || !Number.isFinite(xy[1])) return null;
                return (
                  <g
                    key={m.key}
                    transform={`translate(${xy[0]},${xy[1]})`}
                    className="europe-live-marker"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      setHoverTip({
                        x: e.clientX,
                        y: e.clientY,
                        hoverId: m.hoverId,
                        title: m.tipTitle,
                        sub: m.tipSub,
                        plaatsen: m.plaatsen,
                        cartLines: m.cartLines,
                      });
                    }}
                    onMouseMove={(e) => {
                      setHoverTip((t) =>
                        t && t.hoverId === m.hoverId ? { ...t, x: e.clientX, y: e.clientY } : t
                      );
                    }}
                    onMouseLeave={() => setHoverTip(null)}
                  >
                    <circle
                      r={m.r + 0.75}
                      fill={m.dotHaloFill || '#ef4444'}
                      fillOpacity={m.dotHaloOpacity ?? 0.22}
                      className={loading ? 'europe-dot-pulse' : ''}
                    />
                    <circle
                      r={m.r}
                      fill={m.dotCoreFill || '#dc2626'}
                      stroke="#fff"
                      strokeWidth={0.45}
                    >
                      <title>{`${m.tipTitle} — ${m.tipSub}. ${m.plaatsen}${
                        m.funnel === 'checkout'
                          ? ' · Checkout of winkelmand in dit venster.'
                          : m.funnel === 'cart'
                            ? ' · Winkelmand in dit venster.'
                            : ''
                      }${m.cartLines && m.cartLines.length ? ` Winkelwagen: ${m.cartLines.join('; ')}` : ''}`}</title>
                    </circle>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
        {hoverTip && (
          <div
            className="europe-live-map-tooltip"
            style={{ left: hoverTip.x + 14, top: hoverTip.y + 14 }}
            role="tooltip"
          >
            <div className="europe-live-map-tooltip-title">{hoverTip.title}</div>
            <div className="europe-live-map-tooltip-sub">{hoverTip.sub}</div>
            <div className="europe-live-map-tooltip-places">{hoverTip.plaatsen}</div>
            {Array.isArray(hoverTip.cartLines) && hoverTip.cartLines.length > 0 && (
              <div className="europe-live-map-tooltip-cart">
                <div className="europe-live-map-tooltip-cart-label">Winkelwagen</div>
                <ul className="europe-live-map-tooltip-cart-list">
                  {hoverTip.cartLines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <div className="europe-live-map-legend">
          {!loading && markers.length === 0 && !mapError && (
            <span className="europe-live-map-muted">Geen bezoekers met bekend Europees land in dit venster.</span>
          )}
          {total > 0 && (
            <span className="europe-live-map-total">
              {useDots ? (
                <>
                  Actieve bezoekers (op kaart): <strong>{total}</strong>
                </>
              ) : (
                <>
                  Totaal (getoonde landen): <strong>{total}</strong>
                </>
              )}
            </span>
          )}
          {loading && <span className="europe-live-map-muted">Bijwerken…</span>}
          {useDots && !loading && total > 0 && (
            <span className="europe-live-map-funnel-hint">
              Rood = browsen · Groen = winkelmand in venster · Donkergroen = checkout in venster.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
